<template>
  <div id="page-recu">

    <!-- Boutons -->
    <div class="text-end my-3">
      <button class="btn btn-outline-primary me-2" @click="imprimer">
        <i class="bi bi-printer me-1"></i> Imprimer
      </button>
      <button class="btn btn-outline-success" @click="telechargerPDF">
        <i class="bi bi-download me-1"></i> Télécharger
      </button>
    </div>

    <!-- Aperçu à l'écran (l'impression utilise la mise en page commune) -->
    <div class="card shadow-sm">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-start border-bottom pb-2 mb-3">
          <div class="d-flex align-items-center gap-2">
            <img v-if="logoUrl" :src="logoUrl" alt="Logo" class="logo" />
            <div>
              <h5 class="mb-0 text-primary">{{ systeme?.nom || "Clinique" }}</h5>
              <small class="text-muted">{{ systeme?.adresse || "" }}<span v-if="systeme?.telephone"> · Tél. {{ systeme.telephone }}</span></small>
            </div>
          </div>
          <div class="text-end">
            <div class="fw-bold text-uppercase">Reçu d'hospitalisation</div>
            <small>N° {{ reference }}<br>Date : {{ formatDate(hospitalisation.dateEntree) }}</small>
          </div>
        </div>

        <h6 class="text-primary">Informations du patient</h6>
        <p class="mb-1"><strong>Nom :</strong> {{ nomPatient || "—" }}</p>
        <p class="mb-1"><strong>Chambre :</strong> {{ hospitalisation.chambre?.numero || "—" }}
          &nbsp;&nbsp; <strong>Lit :</strong> {{ hospitalisation.lit?.numero || "—" }}</p>
        <p class="mb-3">
          <strong>Montant :</strong> {{ montant }}
          &nbsp;&nbsp; <strong>Statut :</strong> {{ hospitalisation.estPaye ? "Payé" : "Non payé" }}
        </p>

        <h6 class="text-primary">Détails de l'hospitalisation</h6>
        <table class="table table-sm table-bordered">
          <tbody>
            <tr><td>Date d'entrée</td><td>{{ formatDate(hospitalisation.dateEntree) }}</td></tr>
            <tr><td>Date de sortie prévue</td><td>{{ formatDate(hospitalisation.dateSortiePrevue) }}</td></tr>
            <tr><td>Date de sortie réelle</td><td>{{ formatDate(hospitalisation.dateSortieReelle) }}</td></tr>
          </tbody>
        </table>

        <div class="text-center mt-3" ref="qr">
          <qrcode-vue :value="hospitalisation.id?.toString() || ''" :size="110" />
          <p class="text-muted mt-2">ID : {{ hospitalisation.id }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/api";
import dayjs from "dayjs";
import QRCodeVue from "qrcode.vue";
import { imprimer, telechargerPdf, configClinique, urlFichier, e, fcfa, blocInfos, nomComplet } from "@/impression/impression";

export default {
  components: { "qrcode-vue": QRCodeVue },
  data() {
    return {
      hospitalisation: {},
      reference: "",
      systeme: null,
    };
  },
  computed: {
    logoUrl() {
      return urlFichier(this.systeme?.logo);
    },
    nomPatient() {
      return nomComplet(this.hospitalisation.patient);
    },
    montant() {
      return fcfa(this.hospitalisation.montantFacture);
    },
  },
  created() {
    this.getHospitalisation();
    this.getConfigurationSysteme();
  },
  methods: {
    formatDate(date) {
      return date ? dayjs(date).format("DD-MM-YYYY HH:mm") : "—";
    },
    async getHospitalisation() {
      const { id } = this.$route.params;
      const res = await api.getItem("hospitalisations", id);
      this.hospitalisation = res.data || {};
      this.reference = this.hospitalisation.numHospitalisation
        || `HOS-${dayjs().format("YYYYMMDD")}-${id.toString().padStart(5, "0")}`;
    },
    async getConfigurationSysteme() {
      this.systeme = await configClinique();
    },
    /** Options communes à l'impression et au PDF (format A5). */
    optionsDocument() {
      const h = this.hospitalisation || {};
      const canvas = this.$refs.qr?.querySelector("canvas");
      let qr = "";
      try {
        qr = canvas ? canvas.toDataURL("image/png") : "";
      } catch (err) {
        qr = "";
      }
      const corps = `
        <div class="section">Informations du patient</div>
        ${blocInfos([
          ["Nom", nomComplet(h.patient) || "—"],
          ["Chambre", h.chambre?.numero || "—"],
          ["Lit", h.lit?.numero || "—"],
          ["Montant", fcfa(h.montantFacture)],
          ["Statut", h.estPaye ? '<span class="badge-ok">Payé</span>' : '<span class="badge-ko">Non payé</span>', true],
        ])}
        <div class="section">Détails de l'hospitalisation</div>
        <table class="lignes">
          <tbody>
            <tr><td>Date d'entrée</td><td>${e(this.formatDate(h.dateEntree))}</td></tr>
            <tr><td>Date de sortie prévue</td><td>${e(this.formatDate(h.dateSortiePrevue))}</td></tr>
            <tr><td>Date de sortie réelle</td><td>${e(this.formatDate(h.dateSortieReelle))}</td></tr>
          </tbody>
        </table>
        ${qr ? `<div class="centre" style="margin-top:12px"><img src="${qr}" alt="" style="width:90px;height:90px"><div class="muted">ID : ${e(h.id)}</div></div>` : ""}`;
      return {
        titre: "Reçu d'hospitalisation",
        numero: this.reference,
        date: this.formatDate(h.dateEntree),
        corps,
        signature: true,
        format: "A5",
      };
    },
    async imprimer() {
      await imprimer(this.optionsDocument());
    },
    async telechargerPDF() {
      await telechargerPdf(this.optionsDocument(), `${this.reference || "recu-hospitalisation"}.pdf`);
    },
  },
};
</script>

<style scoped>
.logo {
  width: 60px;
  height: 60px;
  object-fit: contain;
}
</style>
